import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { IAlbum, ITrack, SellStatus } from "../../../hooks/albums";
import {
  Box,
  Card,
  Fade,
  IconButton,
  LinearProgress,
  List,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  useTheme
} from "@mui/material";
import { useGetImage, useUploadImage } from "../../../hooks/images";
import { strings } from "../../../localization/LocalizedStrings";
import { AlbumHeaderListSection } from "../AlbumHeaderListSection";
import { AlbumLabelListSection } from "../AlbumLabelListSection";
import { AlbumYearListSection } from "../AlbumYearListSection";
import { AlbumGradesListSection } from "../AlbumGradesListSection";
import { AlbumFinancialsListSection } from "../AlbumFinancialsListSection";
import { AlbumFormatListSection } from "../AlbumFormatListSection";
import { AlbumImpactListSection } from "../AlbumImpactListSection";
import { ImageDropzone } from "../../ImageDropzone";
import { TrackListHeaderListItem } from "../../attributes/TrackListHeaderListItem";
import { AlbumTrackList } from "../AlbumTrackList";
import { useFetchTracks } from "../../../hooks/albums/useFetchTracks";
import { StatusCodes } from "http-status-codes";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { ImageCache } from "../../../models/ImageCache";
import { AlbumNotesListSection } from "../AlbumNotesListSection";
import { useUpdateAlbum } from "../../../hooks/albums/useUpdateAlbum";
import { AlertDialog } from "../../AlertDialog";
import { AlbumCollectionsListSection } from "../AlbumCollectionsListSection";
import { toast } from "react-toastify";
import { ShortcutsGrid } from "../../ShortcutsGrid";
import { useIsMobile } from "../../../hooks/isMobile";
import { useUser, useUserProfile } from "../../../hooks/session";
import { ImageDetailDialog } from "../../ImageDetailDialog";
import { useContextMenu } from "../../../hooks/contextMenu";
import { ImageGalleryDialog } from "../imageGallery/ImageGalleryDialog";
import useLongPress from "../../../hooks/longPress";
import { AlbumUtilities } from "../../../models/AlbumUtilities";
import { AlbumMessagesListSection } from "../AlbumMessagesListSection";
import { useCurrentPublicCollection } from "../../../hooks/collections/useCollectionManager";
import { AlbumLocationListSection } from "../AlbumLocationListSection";
import { BoxSetAlbumList } from "../BoxSetAlbumList";
import { AlbumInsightsListSection } from "../AlbumInsightsListSection";
import { useUploadFrontImageFromDiscogs } from "../../../hooks/albums/useUploadFrontImageFromDiscogs";
import { useCheckDiscogsAccess } from "../../../hooks/discogs/useCheckDiscogsAccess";
import { Icon, IconType } from "../../../icons";
import { Album3DRepresentationFetcher } from "../Album3DRepresentation/Fetcher";
import { Album3DRepresentationContainerHandle } from "../Album3DRepresentation/Container";

export const AlbumDetail: FunctionComponent<IAlbumDetailProps> = ({
  isPublic,
  isLoadingSharedAlbum,
  album,
  onAttributeUpdated,
  onArtistUpdated,
  onPlayFromYouTube,
  onNewAlbum,
  onScanBarcode,
  onImportFromDiscogs,
  onImportFromExcel,
  onFilterAlbums,
  onRandomAlbums,
  onChangeCollection,
  onEditProfile,
  onCopy,
  onDelete,
  onMarkAs,
  onDeleteFromBoxSet,
  onAddAlbumToBoxSet,
  onAlbumSelected,
  onBoxSetSelected,
  setLoading
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [currentUser] = useUser.useState();
  const [currentUserProfile] = useUserProfile.useState();
  const [currentPublicCollection] = useCurrentPublicCollection.useState();
  const { getImage } = useGetImage();
  const { updateAlbum } = useUpdateAlbum();
  const { uploadImage } = useUploadImage();
  const { fetchTracks } = useFetchTracks();
  const { uploadFrontImageFromDiscogs } = useUploadFrontImageFromDiscogs();
  const [isLoadingTrackList, setIsLoadingTrackList] = useState(false);
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const [albumImage, setAlbumImage] = useState<string>();
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [shouldOpenFileDialog, setShouldOpenFileDialog] = useState(false);
  const [isImageGalleryDialogOpen, setIsImageGalleryDialogOpen] = useState(false);
  const [isImageDetailDialogOpen, setIsImageDetailDialogOpen] = useState(false);
  const [detailImageId, setDetailImageId] = useState<string>();
  const [detailImage, setDetailImage] = useState<string>();
  const [detailImageTitle, setDetailImageTitle] = useState("");
  const [detailImageFilename, setDetailImageFilename] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [isFetchImageConfirmationAlertOpen, setIsFetchImageConfirmationAlertOpen] = useState(false);
  const genericSleeveImage = new URL("../../../images/generic_sleeve.jpg", import.meta.url).href;
  const currentAlbumUuid = useRef("");
  const fetchAlbumImage = async () => {
    currentAlbumUuid.current = album?.uuid;
    setAlbumImage(undefined);
    if (album === undefined || !album.hasImage) {
      setIsLoadingImage(false);
      return;
    }
    setIsLoadingImage(true);
    const response = await getImage({
      name: album.uuid,
      width: ImageCache.bigAlbumImageSize,
      height: ImageCache.bigAlbumImageSize
    });
    // Discard image if album changed
    if (currentAlbumUuid.current != album?.uuid) return;

    if (StatusCodesHelper.isSuccessful(response.status)) {
      setAlbumImage(response.body);
    } else {
      setAlbumImage(undefined);
    }
    setIsLoadingImage(false);
  };
  const fetchTrackListFromDiscogs = async () => {
    if (album === undefined) return;
    setIsLoadingTrackList(true);
    const { status, body } = await fetchTracks(album.uuid);
    if (StatusCodesHelper.isSuccessful(status)) {
      toast.success(strings.album_update_success);
      album = body;
      onAttributeUpdated(body);
    } else if (status == StatusCodes.UNPROCESSABLE_ENTITY) {
      album.trackList = [];
      onAttributeUpdated(album);
    } else {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    }
    setIsLoadingTrackList(false);
  };
  const updateTrackList = async (newTrackList: ITrack[]) => {
    if (album === undefined) return;
    setIsLoadingTrackList(true);
    const { status, body } = await updateAlbum({ uuid: album.uuid, albumTracks: newTrackList });
    if (StatusCodesHelper.isSuccessful(status)) {
      toast.success(strings.album_update_success);
      album.trackList = newTrackList;
    } else {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    }
    setIsLoadingTrackList(false);
  };
  const onOpenImageDetail = (id: string, image: string, title: string, filename: string) => {
    setDetailImageId(id);
    setDetailImage(image);
    setDetailImageTitle(title);
    setDetailImageFilename(filename);
    setIsImageDetailDialogOpen(true);
  };
  const onImageFileChanged = async (file: File) => {
    album3DRepresentationRef.current?.reset();
    setIsLoadingImage(true);
    const newThumbnail = await uploadImage(
      { name: album.uuid, file },
      ImageCache.bigAlbumImageSize,
      ImageCache.bigAlbumImageSize,
      ImageCache.smallAlbumImageSize,
      ImageCache.smallAlbumImageSize
    );
    if (newThumbnail) {
      album.hasImage = true;
      album.thumbnail = newThumbnail;
      onAttributeUpdated(album);
      await fetchAlbumImage();
    }
    setIsLoadingImage(false);
  };
  const willFetchImageFromDiscogs = async () => {
    if (album.hasImage) {
      setIsFetchImageConfirmationAlertOpen(true);
    } else {
      await fetchImageFromDiscogs();
    }
  };
  const fetchImageFromDiscogs = async () => {
    if (isLoadingImage) return;
    setIsLoadingImage(true);
    const { status, body } = await uploadFrontImageFromDiscogs(album.uuid);
    if (StatusCodesHelper.isSuccessful(status)) {
      album.hasImage = true;
      album.thumbnail = body;
      onAttributeUpdated(album);
      await fetchAlbumImage();
    } else {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    }
    setIsLoadingImage(false);
  };
  const { checkDiscogsAccess } = useCheckDiscogsAccess(
    setAlertMessage,
    setAlertIsOpen,
    setIsLoadingImage,
    willFetchImageFromDiscogs
  );
  useEffect(() => {
    fetchAlbumImage().then();
  }, [album?.uuid]);
  const handleLongPress = useLongPress({ onLongPress: handleContextMenu });
  const album3DRepresentationRef = useRef<Album3DRepresentationContainerHandle>(null);
  return (
    <Card
      className={styles.mainContainer}
      style={{ backgroundColor: theme.palette.background.default }}
    >
      {isLoadingSharedAlbum ? (
        <Fade in unmountOnExit style={{ transitionDelay: "1000ms" }}>
          <Stack className={styles.loadingStack} spacing={3}>
            <p>{strings.album_shared_loading}</p>
            <LinearProgress className={styles.progressBar} variant={"indeterminate"} />
          </Stack>
        </Fade>
      ) : !album ? (
        currentUser && (
          <Stack className={styles.noSelectionDiv}>
            {isPublic && currentPublicCollection && (
              <Card
                className={styles.publicCollectionCard}
                elevation={4}
                style={{ backgroundColor: theme.palette.background.default }}
              >
                <p className={styles.publicCollectionName}>{currentPublicCollection.name}</p>
                <p
                  className={styles.publicCollectionDescription}
                  style={{ color: theme.palette.primary.subtitle }}
                >
                  {currentPublicCollection.description}
                </p>
              </Card>
            )}
            <p className={styles.shortcutsTitle}>{strings.shortcuts_title}</p>
            <ShortcutsGrid
              className={styles.shortcutsGrid}
              isPublic={isPublic}
              showAttributesShortcut={true}
              onNewAlbum={onNewAlbum}
              onScanBarcode={onScanBarcode}
              onImportFromDiscogs={onImportFromDiscogs}
              onImportFromExcel={onImportFromExcel}
              onFilterAlbums={onFilterAlbums}
              onRandomAlbums={onRandomAlbums}
              onChangeCollection={onChangeCollection}
              onEditProfile={onEditProfile}
            />
          </Stack>
        )
      ) : (
        <Stack className={styles.mainDiv} direction={isMobile ? "column" : "row"}>
          <Stack display={"flex"}>
            <div
              className={styles.albumImageCard}
              style={{ width: isMobile ? "100%" : 360 }}
              onContextMenu={handleContextMenu}
              {...handleLongPress}
            >
              {album.sellStatus && !["new", "highlighted"].includes(album.sellStatus) && (
                <Box
                  className={styles.reservedBox}
                  style={{
                    width: currentUserProfile?.show3DModel ? "200px" : "100%",
                    height: currentUserProfile?.show3DModel ? "80px" : "100%",
                    borderRadius: currentUserProfile?.show3DModel ? "10px" : undefined,
                    backgroundColor: AlbumUtilities.getSellStatusBackgroundColor(
                      theme,
                      album.sellStatus
                    )
                  }}
                >
                  <p className={styles.reservedText}>
                    {AlbumUtilities.getSellStatusDescription(album.sellStatus)}
                  </p>
                </Box>
              )}
              <Stack
                className={styles.hoverButtonsStack}
                style={{ visibility: isDraggingImage ? "hidden" : undefined }}
                spacing={1}
              >
                <Tooltip title={strings.image_gallery} arrow disableInteractive>
                  <IconButton
                    className={styles.hoverButton}
                    style={{ backgroundColor: theme.palette.colors?.counterBackground }}
                    onClick={() => setIsImageGalleryDialogOpen(true)}
                  >
                    <Icon type={IconType.GalleryThumbnail} />
                  </IconButton>
                </Tooltip>
                {albumImage && (
                  <Tooltip title={strings.option_view_image} arrow disableInteractive>
                    <IconButton
                      className={styles.hoverButton}
                      style={{ backgroundColor: theme.palette.colors?.counterBackground }}
                      onClick={() =>
                        onOpenImageDetail(album.uuid, albumImage, album.name, album.name)
                      }
                    >
                      <Icon type={IconType.ImageSearch} />
                    </IconButton>
                  </Tooltip>
                )}
                {!isPublic && (
                  <Tooltip title={strings.modify_front_image} arrow disableInteractive>
                    <IconButton
                      className={styles.hoverButton}
                      style={{ backgroundColor: theme.palette.colors?.counterBackground }}
                      onClick={() => setShouldOpenFileDialog(state => !state)}
                    >
                      <Icon type={IconType.EditSquare} />
                    </IconButton>
                  </Tooltip>
                )}
                {!isPublic && album?.discogsReleaseId && (
                  <Tooltip title={strings.fetch_image_from_discogs} arrow disableInteractive>
                    <IconButton
                      className={styles.hoverButton}
                      style={{ backgroundColor: theme.palette.colors?.counterBackground }}
                      onClick={checkDiscogsAccess}
                    >
                      <Icon type={IconType.CloudDownload} />
                    </IconButton>
                  </Tooltip>
                )}
                {currentUserProfile?.show3DModel && (
                  <Tooltip title={strings.rotation_reset} arrow disableInteractive>
                    <IconButton
                      className={styles.hoverButton}
                      style={{ backgroundColor: theme.palette.colors?.counterBackground }}
                      onClick={() => album3DRepresentationRef.current?.reset()}
                    >
                      <Icon type={IconType.Rotate90DegreesCcw} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              {!currentUserProfile?.show3DModel ? (
                <ImageDropzone
                  disabled={isPublic || isLoadingImage}
                  className={styles.albumImage}
                  onImageChanged={onImageFileChanged}
                  placeholderImage={genericSleeveImage}
                  isLoading={isLoadingImage}
                  initialImage={
                    albumImage
                      ? `data:image/png;base64,${albumImage}`
                      : album.thumbnail
                        ? `data:image/png;base64,${album.thumbnail}`
                        : undefined
                  }
                  shouldOpenFileDialog={shouldOpenFileDialog}
                  onClick={() => setIsImageGalleryDialogOpen(true)}
                />
              ) : (
                <Album3DRepresentationFetcher
                  ref={album3DRepresentationRef}
                  album={album}
                  defaultFrontImage={albumImage ?? album.thumbnail}
                  isPublic={isPublic}
                  size={360}
                  disabled={isPublic || isLoadingImage}
                  onImageChanged={onImageFileChanged}
                  isLoading={isLoadingImage}
                  setIsLoading={setIsLoadingImage}
                  shouldOpenFileDialog={shouldOpenFileDialog}
                  setIsDragging={setIsDraggingImage}
                />
              )}
            </div>
            <TrackListHeaderListItem
              isPublic={isPublic}
              className={styles.tracksHeader}
              album={album}
              onDiscogsIdUpdated={async newValue => {
                if (
                  newValue !== null &&
                  (album?.trackList == undefined || album?.trackList?.length == 0) &&
                  !album.isBoxSet
                ) {
                  await fetchTrackListFromDiscogs();
                }
                onAttributeUpdated(album);
              }}
              onLengthUpdated={() => onAttributeUpdated(album)}
              onTracksUpdated={() => onAttributeUpdated(album)}
              onCopy={onCopy}
              onMarkAs={onMarkAs}
              onDelete={onDelete}
              onPlayFromYouTube={videoIds => onPlayFromYouTube(album.name, videoIds)}
            />
            {!(isPublic && !album.trackList) && !album.isBoxSet && (
              <AlbumTrackList
                key={album.uuid}
                isPublic={isPublic}
                className={isMobile ? styles.trackListMobile : styles.trackList}
                album={album}
                artistName={album.artist?.name}
                albumName={album.name}
                fetchFromDiscogsEnabled={
                  album.discogsReleaseId !== undefined && album.discogsReleaseId != ""
                }
                isLoading={isLoadingTrackList}
                onChange={async newTrackList => {
                  await updateTrackList(newTrackList);
                  onAttributeUpdated(album);
                }}
                onPlayFromYouTube={videoIds => onPlayFromYouTube(album.name, videoIds)}
                onFetchTrackListFromDiscogs={fetchTrackListFromDiscogs}
              />
            )}
            {album.isBoxSet && (
              <BoxSetAlbumList
                isPublic={isPublic}
                className={isMobile ? styles.trackListMobile : styles.trackList}
                boxSet={album}
                onDeleteFromBoxSet={onDeleteFromBoxSet}
                onAlbumSelected={onAlbumSelected}
                setLoading={setLoading}
                onAddAlbum={onAddAlbumToBoxSet}
              />
            )}
          </Stack>
          <List
            disablePadding
            className={isMobile ? styles.mobileList : styles.list}
            style={{ overflow: isMobile ? "unset" : "auto" }}
          >
            <Card
              elevation={5}
              className={isMobile ? styles.firstItemCardMobile : styles.firstItemCard}
              style={{ backgroundColor: theme.palette.background.default }}
            >
              <AlbumHeaderListSection
                key={"header"}
                isPublic={isPublic}
                album={album}
                onNameUpdated={onAttributeUpdated}
                onArtistUpdated={onArtistUpdated}
              />
            </Card>
            <Card
              elevation={5}
              className={isMobile ? styles.itemCardMobile : styles.itemCard}
              style={{ backgroundColor: theme.palette.background.default }}
            >
              <AlbumLabelListSection
                key={"label"}
                isPublic={isPublic}
                album={album}
                onAttributeUpdated={onAttributeUpdated}
              />
            </Card>
            <Card
              elevation={5}
              className={isMobile ? styles.itemCardMobile : styles.itemCard}
              style={{ backgroundColor: theme.palette.background.default }}
            >
              <AlbumYearListSection
                key={"year"}
                isPublic={isPublic}
                album={album}
                onAttributeUpdated={onAttributeUpdated}
              />
            </Card>
            <Card
              elevation={5}
              className={isMobile ? styles.itemCardMobile : styles.itemCard}
              style={{ backgroundColor: theme.palette.background.default }}
            >
              <AlbumFormatListSection
                key={"format"}
                isPublic={isPublic}
                album={album}
                onAttributeUpdated={onAttributeUpdated}
              />
            </Card>
            <Card
              elevation={5}
              className={isMobile ? styles.itemCardMobile : styles.itemCard}
              style={{ backgroundColor: theme.palette.background.default }}
            >
              <AlbumGradesListSection
                key={"grades"}
                isPublic={isPublic}
                isMobile={isMobile}
                album={album}
                onAttributeUpdated={onAttributeUpdated}
              />
            </Card>
            <Card
              elevation={5}
              className={isMobile ? styles.itemCardMobile : styles.itemCard}
              style={{ backgroundColor: theme.palette.background.default }}
            >
              <AlbumFinancialsListSection
                key={"financials"}
                isPublic={isPublic}
                album={album}
                onAttributeUpdated={onAttributeUpdated}
              />
            </Card>
            {!isPublic && (
              <Card
                elevation={5}
                className={isMobile ? styles.itemCardMobile : styles.itemCard}
                style={{ backgroundColor: theme.palette.background.default }}
              >
                <AlbumLocationListSection
                  key={"location"}
                  album={album}
                  onBoxSetSelected={onBoxSetSelected}
                />
              </Card>
            )}
            {!isPublic && (
              <Card
                elevation={5}
                className={isMobile ? styles.itemCardMobile : styles.itemCard}
                style={{ backgroundColor: theme.palette.background.default }}
              >
                <AlbumCollectionsListSection key={"collections"} album={album} />
              </Card>
            )}
            {!(isPublic && album.impact === undefined) && (
              <Card
                elevation={5}
                className={isMobile ? styles.itemCardMobile : styles.itemCard}
                style={{ backgroundColor: theme.palette.background.default }}
              >
                <AlbumImpactListSection
                  key={"impact"}
                  isPublic={isPublic}
                  album={album}
                  onAttributeUpdated={onAttributeUpdated}
                />
              </Card>
            )}
            {!(isPublic && album.notes === undefined) && (
              <Card
                elevation={5}
                className={isMobile ? styles.itemCardMobile : styles.itemCard}
                style={{ backgroundColor: theme.palette.background.default }}
              >
                <AlbumNotesListSection
                  key={"notes"}
                  isPublic={isPublic}
                  album={album}
                  onAttributeUpdated={onAttributeUpdated}
                />
              </Card>
            )}
            {!isPublic && (
              <Card
                elevation={5}
                className={isMobile ? styles.itemCardMobile : styles.itemCard}
                style={{ backgroundColor: theme.palette.background.default }}
              >
                <AlbumInsightsListSection key={"insights"} album={album} />
              </Card>
            )}
            <Card
              elevation={5}
              className={isMobile ? styles.lastItemCardMobile : styles.lastItemCard}
              style={{ backgroundColor: theme.palette.background.default }}
            >
              <AlbumMessagesListSection key={"messages"} isPublic={isPublic} album={album} />
            </Card>
            {currentUserProfile?.shouldShowAlbumId && (
              <p className={styles.albumId} style={{ color: theme.palette.secondary.main }}>
                ID: {album.uuid}
              </p>
            )}
          </List>
        </Stack>
      )}
      <Menu {...menuProps}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setIsImageGalleryDialogOpen(true);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.GalleryThumbnail} />
          </ListItemIcon>
          {strings.image_gallery}
        </MenuItem>
        {album && albumImage && (
          <div>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                onOpenImageDetail(album.uuid, albumImage, album.name, album.name);
              }}
              dense
            >
              <ListItemIcon>
                <Icon type={IconType.ImageSearch} />
              </ListItemIcon>
              {strings.option_view_image}
            </MenuItem>
          </div>
        )}
        {!isPublic && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              setShouldOpenFileDialog(state => !state);
            }}
            dense
          >
            <ListItemIcon>
              <Icon type={IconType.EditSquare} />
            </ListItemIcon>
            {strings.modify_front_image}
          </MenuItem>
        )}
        {!isPublic && album?.discogsReleaseId && (
          <MenuItem
            onClick={async () => {
              handleMenuClose();
              await checkDiscogsAccess();
            }}
            dense
          >
            <ListItemIcon>
              <Icon type={IconType.CloudDownload} />
            </ListItemIcon>
            {strings.fetch_image_from_discogs}
          </MenuItem>
        )}
        {currentUserProfile?.show3DModel && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              album3DRepresentationRef.current?.reset();
            }}
            dense
          >
            <ListItemIcon>
              <Icon type={IconType.Rotate90DegreesCcw} />
            </ListItemIcon>
            {strings.rotation_reset}
          </MenuItem>
        )}
        {!isPublic && album?.sellStatus && (
          <MenuItem
            key={"RemoveMark"}
            onClick={async () => {
              handleMenuClose();
              onMarkAs(null);
            }}
            dense
          >
            <ListItemIcon>
              <Icon type={IconType.KeepOff} />
            </ListItemIcon>
            {strings.remove_mark_title}
          </MenuItem>
        )}
      </Menu>
      {album && (
        <ImageGalleryDialog
          isPublic={isPublic}
          album={album}
          isOpen={isImageGalleryDialogOpen}
          setIsOpen={setIsImageGalleryDialogOpen}
          onAlbumImageUpdated={async albumImage => {
            album.hasImage = true;
            album.thumbnail = albumImage.thumbnail;
            onAttributeUpdated(album);
            await fetchAlbumImage();
          }}
          onAdditionalAlbumImageUpdated={() => album3DRepresentationRef.current?.refreshImages()}
          onOpenImageDetail={onOpenImageDetail}
        />
      )}
      <ImageDetailDialog
        isPublic={isPublic}
        uuid={detailImageId}
        title={detailImageTitle}
        isOpen={isImageDetailDialogOpen}
        setIsOpen={setIsImageDetailDialogOpen}
        filename={detailImageFilename}
        image={detailImage}
      />
      <AlertDialog
        message={strings.image_fetch_confirmation}
        open={isFetchImageConfirmationAlertOpen}
        setOpen={setIsFetchImageConfirmationAlertOpen}
        isConfirm
        onConfirm={fetchImageFromDiscogs}
      />
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
    </Card>
  );
};

export interface IAlbumDetailProps {
  isPublic: boolean;
  isLoadingSharedAlbum: boolean;
  album?: IAlbum;
  onAttributeUpdated: (album: IAlbum) => void;
  onArtistUpdated: () => void;
  onPlayFromYouTube: (title: string, videoIds: string[]) => void;
  onNewAlbum: () => void;
  onScanBarcode: () => void;
  onImportFromDiscogs: () => void;
  onImportFromExcel: () => void;
  onFilterAlbums: () => void;
  onRandomAlbums: () => void;
  onChangeCollection: () => void;
  onEditProfile: () => void;
  onCopy: () => void;
  onMarkAs: (sellStatus: SellStatus | null) => void;
  onDelete: () => void;
  onDeleteFromBoxSet?: (newBoxSet: IAlbum) => void;
  onAddAlbumToBoxSet: (album: IAlbum) => void;
  onAlbumSelected: (album: IAlbum) => void;
  onBoxSetSelected: () => void;
  setLoading: (isLoading: boolean) => void;
}
